import type { TerrainChange } from '../network/gameStateSync';

/**
 * Canvas-free terrain collision mask backed by a flat Uint8Array of alpha
 * values (0..255, solid when > 128). Runs identically in the browser and in
 * Node, which is what allows the server to be simulation-authoritative.
 */
export class CollisionMask {
  protected readonly maskWidth: number;
  protected readonly maskHeight: number;
  protected alpha: Uint8Array;
  private modifications: TerrainChange[] = [];
  private terrainVersion: number = 0;

  constructor(width: number, height: number) {
    this.maskWidth = width;
    this.maskHeight = height;
    this.alpha = new Uint8Array(width * height);
  }

  /** Bulk-import alpha values (e.g. extracted from a canvas or received over the network). */
  setAlphaData(data: Uint8Array): void {
    if (data.length !== this.maskWidth * this.maskHeight) {
      throw new Error(
        `CollisionMask.setAlphaData: expected ${this.maskWidth * this.maskHeight} bytes, got ${data.length}`,
      );
    }
    this.alpha = data;
  }

  getAlphaData(): Uint8Array {
    return this.alpha;
  }

  isSolid(x: number, y: number): boolean {
    const ix = Math.floor(x);
    const iy = Math.floor(y);
    if (ix < 0 || ix >= this.maskWidth || iy < 0 || iy >= this.maskHeight) {
      return false;
    }
    return this.alpha[iy * this.maskWidth + ix] > 128;
  }

  destroyCircle(
    cx: number,
    cy: number,
    radius: number,
    type: TerrainChange['type'] = 'explosion',
  ): void {
    this.recordModification(type, cx, cy, radius);
    this.terrainVersion++;
    this.eraseCircle(cx, cy, radius);
  }

  protected eraseCircle(cx: number, cy: number, radius: number): void {
    const r = Math.max(0, radius);
    const minY = Math.max(0, Math.floor(cy - r));
    const maxY = Math.min(this.maskHeight - 1, Math.ceil(cy + r));
    const rSq = r * r;

    for (let y = minY; y <= maxY; y++) {
      const dy = y + 0.5 - cy;
      const span = rSq - dy * dy;
      if (span < 0) continue;
      const half = Math.sqrt(span);
      const minX = Math.max(0, Math.floor(cx - half));
      const maxX = Math.min(this.maskWidth - 1, Math.ceil(cx + half));
      const rowOffset = y * this.maskWidth;
      for (let x = minX; x <= maxX; x++) {
        const dx = x + 0.5 - cx;
        if (dx * dx + dy * dy <= rSq) {
          this.alpha[rowOffset + x] = 0;
        }
      }
    }
  }

  raycast(x0: number, y0: number, x1: number, y1: number): { hit: boolean; x: number; y: number } {
    const dx = Math.abs(x1 - x0);
    const dy = Math.abs(y1 - y0);
    const sx = x0 < x1 ? 1 : -1;
    const sy = y0 < y1 ? 1 : -1;
    let err = dx - dy;
    let x = Math.floor(x0);
    let y = Math.floor(y0);
    const targetX = Math.floor(x1);
    const targetY = Math.floor(y1);

    while (true) {
      if (this.isSolid(x, y)) {
        return { hit: true, x, y };
      }
      if (x === targetX && y === targetY) break;
      const e2 = 2 * err;
      if (e2 > -dy) {
        err -= dy;
        x += sx;
      }
      if (e2 < dx) {
        err += dx;
        y += sy;
      }
    }
    return { hit: false, x: x1, y: y1 };
  }

  findGround(x: number, startY: number, maxDistance: number): number | null {
    for (let dy = 0; dy < maxDistance; dy++) {
      if (this.isSolid(x, startY + dy)) {
        return startY + dy;
      }
    }
    return null;
  }

  getSurfaceNormal(x: number, y: number, vx = 0, vy = 0): { x: number; y: number } {
    let surfaceX = x;
    let surfaceY = y;

    if (this.isSolid(x, y)) {
      const len = Math.sqrt(vx * vx + vy * vy);
      if (len > 0) {
        const dirX = -vx / len;
        const dirY = -vy / len;
        for (let i = 1; i <= 10; i++) {
          const testX = x + dirX * i;
          const testY = y + dirY * i;
          if (!this.isSolid(testX, testY)) {
            surfaceX = testX;
            surfaceY = testY;
            break;
          }
        }
      }
    }

    const sampleRadius = 4;
    let nx = 0;
    let ny = 0;

    for (let dx = -sampleRadius; dx <= sampleRadius; dx++) {
      for (let dy = -sampleRadius; dy <= sampleRadius; dy++) {
        if (this.isSolid(surfaceX + dx, surfaceY + dy)) {
          nx -= dx;
          ny -= dy;
        }
      }
    }

    const normLen = Math.sqrt(nx * nx + ny * ny);
    if (normLen === 0) return { x: 0, y: -1 };
    return { x: nx / normLen, y: ny / normLen };
  }

  getWidth(): number {
    return this.maskWidth;
  }

  getHeight(): number {
    return this.maskHeight;
  }

  private recordModification(
    type: TerrainChange['type'],
    x: number,
    y: number,
    radius: number,
  ): void {
    this.modifications.push({
      type,
      x: Math.round(x),
      y: Math.round(y),
      radius: Math.round(radius),
      timestamp: Date.now(),
    });
  }

  getModifications(): TerrainChange[] {
    return [...this.modifications];
  }

  /**
   * Log a girder placement in the modification history. The girder pixels
   * themselves are rasterized externally (MaskCanvas) onto this mask's alpha;
   * recording it makes the placement replayable on clients that missed the
   * live event (join/reconnect/loading).
   */
  recordGirder(x: number, y: number, angleIndex: number, sizeIndex: number): void {
    this.modifications.push({
      type: 'girder',
      x: Math.round(x),
      y: Math.round(y),
      radius: 0,
      angleIndex,
      sizeIndex,
      timestamp: Date.now(),
    });
    this.terrainVersion++;
  }

  clearModifications(): void {
    this.modifications = [];
  }

  applyModifications(mods: TerrainChange[]): void {
    const sorted = [...mods].sort((a, b) => (a.timestamp ?? 0) - (b.timestamp ?? 0));
    for (const mod of sorted) {
      if (mod.type === 'girder') continue; // rasterized by the caller
      this.eraseCircle(mod.x, mod.y, mod.radius);
    }
  }

  getTerrainVersion(): number {
    return this.terrainVersion;
  }

  getModificationsSince(version: number): TerrainChange[] {
    return this.modifications.slice(version);
  }
}
